import React, { useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Spinner } from '../../components';
import { getFileFromUrl } from '../../../helpers/helpers';
import { externalLetterSchema } from '../../../helpers/validation';
import { replyLetterAsExternalUser } from '../../../config/redux/actions/bank';
import { mapDataToFormData } from '../../../utils/lib';
import { appUrls } from '../../../utils/urls/appUrls';

import Editor from '../../components/Editor';

function DigitalLetterComposeExternal() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const bankUser = useSelector((state) => state.bankUser);

	const getDefaultValues = useCallback(async () => {
		return {
			attached_documents: await getFileFromUrl(
				bankUser.externalUser.letterDetails.ATTACH_DOCUMENTS
			),
			comments: '',
			message: '',
			subject: bankUser.externalUser.letterDetails.LETTER_SUBJECT,
			digital_reference_number:
				bankUser.externalUser.letterDetails.DIGITAL_LETTER_REF,
		};
	}, [bankUser.externalUser.letterDetails]);

	const {
		control,
		register,
		watch,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(externalLetterSchema),
		defaultValues: getDefaultValues,
	});

	const attachedDocument = watch('attached_documents');

	const onSubmit = useCallback(async (data) => {
		const formData = mapDataToFormData(data);
		const resultAction = await dispatch(replyLetterAsExternalUser(formData));

		if (replyLetterAsExternalUser.fulfilled.match(resultAction)) {
			toast.success(resultAction.payload.message, {
				autoClose: 2_000,
				onClose: () => navigate(appUrls.login),
			});
		}
	}, [dispatch, navigate]);

	return (
		<div className="email-right-column app email-body p-2 p-sm-3 p-md-4 p-xl-5">
			<form className="email-compose" onSubmit={handleSubmit(onSubmit)}>
				<div style={{ columnGap: '1.3rem' }} className="border-bottom mb-2 pb-2 d-flex align-items-center flex-wrap">
					<div>
						<Controller
							control={control}
							name="attached_documents"
							render={(props) => (
								<>
									<input
										id="fileUploadPaperClip"
										type="file"
										hidden
										onChange={(e) => props.field.onChange(e.target.files?.[0])}
									/>

									<label
										style={{ fontWeight: 'normal', cursor: 'pointer' }}
										className="mb-0"
										htmlFor="fileUploadPaperClip"
									>
										<i
											style={{ fontSize: '1.3rem', verticalAlign: 'middle' }}
											className="mdi mdi-paperclip mr-1 text-primary"
										></i>
										Attach
									</label>

									{errors.attached_documents && (
										<div className="text-danger small mt-1">
											{errors.attached_documents.message}
										</div>
									)}
								</>
							)}
						/>
					</div>
				</div>

				<div className="email-body-head mb-5 ">
					<h4 className="text-dark">Edit Letter</h4>
				</div>

				<div className="form-group">
					<label htmlFor="subject">Subject</label>

					<input
						type="text"
						className="form-control"
						placeholder="Subject "
						{...register('subject')}
					/>

					{errors.subject && (
						<div className="text-danger small mt-1">
							{errors.subject.message}
						</div>
					)}
				</div>

				<div>
					<label htmlFor="comments">Comment</label>
					<div>
						<Controller
							control={control}
							name="comments"
							render={(props) => (
								<Editor
									value={props.field.value}
									onChange={props.field.onChange}
								/>
							)}
						/>

						{errors.comments && (
							<div className="text-danger small mt-1">
								{errors.comments.message}
							</div>
						)}
					</div>
				</div>

				<div>
					<label htmlFor="message">Compose letter</label>
					<div>
						<Controller
							control={control}
							name="message"
							render={(props) => (
								<Editor
									value={props.field.value}
									onChange={props.field.onChange}
								/>
							)}
						/>

						{errors.message && (
							<div className="text-danger small mt-1">
								{errors.message.message}
							</div>
						)}
					</div>
				</div>

				<div style={{ columnGap: '1rem' }} className="mx-1 mt-4 d-flex row rounded mb-5">
					{attachedDocument && (
						<div className="col-12 col-md-6 col-lg-5">
							<h6 className="mb-2">Attachment</h6>
							<div className="border d-flex justify-content-between p-2">
								<p className="file-name-display">{attachedDocument.name}</p>
								<button className="font-weight-bold" onClick={() => setValue('attached_documents', null)}>
									X
								</button>
							</div>
						</div>
					)}
				</div>

				{bankUser.externalUser.errorMessage && (
					<div className="mb-4 w-100">
						<div className="alert alert-danger" role="alert">
							{bankUser.externalUser.errorMessage}
						</div>
					</div>
				)}

				{bankUser.externalUser.isFetching 
					? <Spinner /> 
					: (
						<button className="btn btn-primary btn-pill mb-5" type="submit">
							Send Letter
						</button>
					)}
			</form>
		</div>
	);
}

export default DigitalLetterComposeExternal;
